import React, { useState, useRef, useMemo } from 'react';
import AccommodationElement from './AccommodationElement';
import AttractionElement from './AttractionElement';
import IndividualTransport from './IndividualTransport';
import TransportElement from './TransportElement';
import TrekkingElement from './TrekkingElement';
import '../../../css/AddTripForm/Element.css';
import { useTripContext } from '../../Trips/TripContext';
import { defaultElements, elementTypes } from '../../../data/Constants';
import { validateElementInputs } from "../validateInputs";
import { useToast } from '../../../Contexts/ToastContext';

const Element = ({ onSave, onDelete, initialData, variantGuid }) => {
  const { tripData, addElementToVariant, updateElement, deleteElementFromVariant, linkElementWithVariant } = useTripContext();
  const [elementData, setElementData] = useState(initialData || {
    ...defaultElements[0], type: 0, variants: []
  });

  const [showPopup, setShowPopup] = useState(false);
  const formRef = useRef(null);
  const { showToast, clearToast } = useToast();
  const [isSaving, setIsSaving] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isLinking, setIsLinking] = useState(false);
  const [linkTypeFilter, setLinkTypeFilter] = useState(-1);
  const [linkVariantFilter, setLinkVariantFilter] = useState(null);


  const [selectedType, setSelectedType] = useState(
    initialData?.type || elementTypes[0].value
  );

  const formatDateTimePl = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("pl-PL", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const toTimestamp = (iso) => {
  if (!iso) return Number.POSITIVE_INFINITY;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? Number.POSITIVE_INFINITY : t;
};


  const handleSave = async () => {
    if (!validateElementInputs(formRef)) return;
    setIsSaving(true);
    showToast('Zapisywanie elementu', 'processing');
    try {
      if (elementData.guid) {
        await updateElement(elementData);
      } else {
        await addElementToVariant(variantGuid, elementData);
      }
      await onSave();
    } finally {
      setIsSaving(false);
      clearToast();
    }
  };

  const handleCancel = () => {
    if (window.confirm('Czy na pewno chcesz odrzucić zmiany?')) {
      onDelete();
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Czy na pewno chcesz usunąć ten element?')) {
      setIsDeleting(true);
      showToast("Usuwanie elementu...", "processing");
      try {
        await deleteElementFromVariant(elementData.guid, variantGuid);
        onDelete();
      } finally {
        setIsDeleting(false);
        clearToast();
      }
    }
  };

  const handleTypeChange = (newType) => {
    newType = Number(newType);
    setSelectedType(newType);
    setElementData({ ...defaultElements[newType], type: newType, variants: [] });
  };

  const handleChange = (field, value) => {
    if (value === null || value === undefined || value === "" || value.trim?.() === "") {
      value = null;
    } else if (typeof value === "boolean" || field === "duration") {
    } else if (!isNaN(value)) {
      value = Number(value);
    }

    setElementData((prev) => ({ ...prev, [field]: value }));
  };

  const renderElementForm = () => {
    switch (selectedType) {
      case 0: return <AttractionElement data={elementData} handleChange={handleChange} />;
      case 1: return <AccommodationElement data={elementData} handleChange={handleChange} />;
      case 2: return <IndividualTransport data={elementData} handleChange={handleChange} />;
      case 3: return <TransportElement data={elementData} handleChange={handleChange} />;
      case 4: return <TrekkingElement data={elementData} handleChange={handleChange} />;
      default: return null;
    }
  };

  const renderPopup = () => {
    const variantOptions = (tripData?.variants || []).filter(v => v.guid !== variantGuid);
const effectiveVariantFilter = linkVariantFilter ?? variantOptions[0]?.guid ?? null;

    const unlinkedElements = (tripData?.elements || []).filter(
      (element) => !element.variants.includes(variantGuid)
    );

  const filteredElements = unlinkedElements
    .filter((e) => {
      if (effectiveVariantFilter) {
        return e.variants.includes(effectiveVariantFilter);
      }
      return true;
    })
    .filter((e) => {
      if (linkTypeFilter === -1) return true;
      return e.type === linkTypeFilter;
    })
    .sort((a, b) => toTimestamp(a.startDate) - toTimestamp(b.startDate));

    return (
      <div className="popup">
        <div className="popupContent">
          <div className="linkPopupHeader">
      <div className="linkPopupTitleRow">
        <div>
          <div className="linkPopupTitle">Powiąż element z wariantem</div>
          <div className="linkPopupHint">
            Wybierz wariant z którego chcesz powiązać element w aktualnym wariancie i następnie wybierz element do powiązania.
          </div>
        </div>
      </div>

  <div className="linkPopupFilters">
    <div className="linkPopupField">
      <label className="linkPopupLabel">Wariant źródłowy</label>
      <select
        value={effectiveVariantFilter ?? ""}
        onChange={(e) => setLinkVariantFilter(e.target.value || null)}
        className="linkPopupSelect"
        disabled={isLinking}
      >
        {variantOptions.map((v) => (
          <option key={v.guid} value={v.guid}>
            {v.name}
          </option>
        ))}
      </select>
    </div>

    <div className="linkPopupField">
      <label className="linkPopupLabel">Typ elementu</label>
      <select
        value={linkTypeFilter}
        onChange={(e) => setLinkTypeFilter(Number(e.target.value))}
        className="linkPopupSelect"
        disabled={isLinking}
      >
        <option value={-1}>Wszystkie</option>
        {elementTypes.map((type) => (
          <option key={type.value} value={type.value}>
            {type.label}
          </option>
        ))}
      </select>
    </div>
  </div>
</div>

          <div className="linkList">
            {filteredElements.map((element) => {
              const typeLabel = elementTypes.find((t) => t.value === element.type)?.label ?? element.type;

              return (
                <div
                  key={element.guid}
                  className={`linkListItem ${isLinking ? "disabled" : ""}`}
                  onClick={async () => {
                    if (isLinking) return;
                    setIsLinking(true);
                    showToast("Wiązanie elementu...", 'processing');
                    try {
                      await linkElementWithVariant(element.guid, variantGuid);
                      onDelete();
                    } finally {
                      setIsLinking(false);
                      clearToast();
                    }
                  }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") e.currentTarget.click();
                  }}
                >
                  <div className="linkListItemTop">
                    <div className="linkListItemLeft">
                      <div className="linkListItemName">{element.name}</div>
                      <div className="linkListItemDate">{formatDateTimePl(element.startDate)}</div>
                    </div>
                    <div className="linkListItemType">{typeLabel}</div>
                  </div>
                </div>
              );
            })}

            {filteredElements.length === 0 && (
              <div className="linkListEmpty">
                Brak elementów do powiązania dla wybranego filtra.
              </div>
            )}
          </div>


          <button onClick={() => setShowPopup(false)} className="closePopup" disabled={isLinking}>
            Zamknij
          </button>
        </div>
      </div>
    );
  };

  return (
    <div className={`elementContainer ${elementData.variants.length > 1 ? "linkedElement" : ""}`} ref={formRef}>
      <div className="elementHeader">
        {!elementData.guid && (
          <>
            <div className="elementHeaderTitle">Dodawanie elementu</div>
            <select value={selectedType} onChange={(e) => handleTypeChange(e.target.value)} className="typeDropdown">
              {elementTypes.map((type) => (
                <option key={type.value} value={type.value}>{type.label}</option>
              ))}
            </select>
          </>
        )}
        {elementData.guid && <div className="elementHeaderTitle">Edycja elementu</div>}
        <div className="actionButtons">
          {!elementData.guid && (
            <div
              onClick={() => {
                const otherVariants = (tripData?.variants || []).filter(v => v.guid !== variantGuid);
                const defaultVariantGuid = otherVariants[0]?.guid ?? null;

                setLinkVariantFilter(defaultVariantGuid);
                setLinkTypeFilter(-1);
                setShowPopup(true);
              }}
              className="linkIconContainer"
            >
              <span className="linkIcon">🔗</span>
              <p className="linkIconText">Powiąż</p>
            </div>
          )}
        </div>
      </div>

      {renderElementForm()}
      {showPopup && renderPopup()}

      <div className="actions">
        {!elementData.guid ? (
          <>
            <button className="btnSecondary" onClick={handleCancel} disabled={isSaving}>Odrzuć zmiany</button>
            <button className="btnPrimary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <><i className="fas fa-spinner fa-spin"></i> Zapisywanie...</> : "Utwórz element"}
            </button>
          </>
        ) : (
          <>
            <button className="btnDanger" onClick={handleDelete} disabled={isDeleting}>
              {isDeleting ? <><i className="fas fa-spinner fa-spin"></i> Usuwanie...</> : (elementData.variants.length > 1 ? "Usuń powiązanie" : "Usuń element")}
            </button>
            <button className="btnSecondary" onClick={handleCancel} disabled={isSaving || isDeleting}>Odrzuć zmiany</button>
            <button className="btnPrimary" onClick={handleSave} disabled={isSaving}>
              {isSaving ? <><i className="fas fa-spinner fa-spin"></i> Zapisywanie...</> : "Zapisz zmiany"}
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default Element;
